import { Skeleton } from "@/components/ui/skeleton";

// Generic page skeleton for most pages
export const PageSkeleton = () => (
  <main className="min-h-screen">
    <div className="container mx-auto px-4 py-8 md:py-16 space-y-8">
      {/* Hero/Header area */}
      <div className="text-center space-y-4 max-w-3xl mx-auto">
        <Skeleton className="h-10 md:h-14 w-3/4 mx-auto" />
        <Skeleton className="h-5 w-full max-w-xl mx-auto" />
        <Skeleton className="h-5 w-2/3 mx-auto" />
      </div>
      
      {/* Content grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Skeleton key={i} className="h-64 rounded-xl" />
        ))}
      </div>
    </div>
  </main>
);

// Landing page skeleton
export const LandingSkeleton = () => (
  <main className="min-h-screen">
    <div className="flex flex-col items-center px-4 py-8 md:py-16 space-y-12">
      {/* Hero */}
      <div className="text-center space-y-6 max-w-4xl">
        <Skeleton className="h-12 md:h-16 w-full max-w-2xl mx-auto" />
        <Skeleton className="h-6 w-3/4 mx-auto" />
        <Skeleton className="h-12 w-48 mx-auto rounded-full" />
      </div>
      
      {/* Bento Grid */}
      <div className="w-full max-w-6xl grid grid-cols-2 md:grid-cols-3 gap-4">
        <Skeleton className="h-32 rounded-xl col-span-2" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl col-span-2" />
      </div>
      
      {/* Articles section */}
      <div className="w-full max-w-7xl">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-72 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  </main>
);

// Article page skeleton
export const ArticleSkeleton = () => (
  <main className="min-h-screen">
    <article className="container mx-auto px-4 py-8 md:py-16 max-w-4xl">
      {/* Breadcrumbs */}
      <Skeleton className="h-4 w-48 mb-6" />
      
      {/* Title */}
      <Skeleton className="h-10 md:h-14 w-full mb-4" />
      <Skeleton className="h-10 md:h-14 w-3/4 mb-6" />
      
      {/* Meta */}
      <div className="flex gap-4 mb-8">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-20" />
      </div>
      
      {/* Hero image */}
      <Skeleton className="h-64 md:h-96 w-full rounded-xl mb-8" />
      
      {/* Content paragraphs */}
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
        <Skeleton className="h-5 w-2/3" />
      </div>
    </article>
  </main>
);

// Magazin/List page skeleton
export const MagazinSkeleton = () => (
  <main className="min-h-screen">
    <div className="container mx-auto px-4 py-8 md:py-16">
      {/* Header */}
      <div className="text-center mb-12">
        <Skeleton className="h-10 md:h-14 w-64 mx-auto mb-4" />
        <Skeleton className="h-5 w-96 max-w-full mx-auto" />
      </div>
      
      {/* Filters */}
      <div className="flex gap-4 mb-8 flex-wrap justify-center">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-48" />
      </div>
      
      {/* Article grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Skeleton key={i} className="h-80 rounded-xl" />
        ))}
      </div>
    </div>
  </main>
);

// Questionnaire skeleton
export const QuestionnaireSkeleton = () => (
  <main className="min-h-screen flex items-center justify-center px-4">
    <div className="w-full max-w-2xl space-y-8">
      {/* Progress bar */}
      <Skeleton className="h-2 w-full rounded-full" />
      
      {/* Question */}
      <div className="text-center space-y-4">
        <Skeleton className="h-8 w-3/4 mx-auto" />
        <Skeleton className="h-5 w-1/2 mx-auto" />
      </div>
      
      {/* Options */}
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-8">
        <Skeleton className="h-12 w-32" />
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  </main>
);

// Results page skeleton
export const ResultsSkeleton = () => (
  <main className="min-h-screen flex items-center justify-center px-4">
    <div className="w-full max-w-lg space-y-6">
      {/* Card */}
      <Skeleton className="h-[500px] w-full rounded-2xl" />
      
      {/* Action buttons */}
      <div className="flex justify-center gap-8">
        <Skeleton className="h-16 w-16 rounded-full" />
        <Skeleton className="h-16 w-16 rounded-full" />
      </div>
      
      {/* Counter */}
      <Skeleton className="h-5 w-32 mx-auto" />
    </div>
  </main>
);

// Products/Category page skeleton
export const ProductsSkeleton = () => (
  <main className="min-h-screen">
    <div className="container mx-auto px-4 py-8 md:py-16">
      {/* Breadcrumbs */}
      <Skeleton className="h-4 w-48 mb-6" />
      
      {/* Header */}
      <div className="text-center mb-12">
        <Skeleton className="h-16 w-16 mx-auto mb-4 rounded-xl" />
        <Skeleton className="h-10 w-64 mx-auto mb-4" />
        <Skeleton className="h-5 w-96 max-w-full mx-auto" />
      </div>
      
      {/* Product grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  </main>
);

// Admin page skeleton
export const AdminSkeleton = () => (
  <main className="min-h-screen">
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      
      {/* Table */}
      <Skeleton className="h-96 rounded-xl" />
    </div>
  </main>
);

// Simple page skeleton (Impressum, Datenschutz, etc.)
export const SimpleSkeleton = () => (
  <main className="min-h-screen">
    <div className="container mx-auto px-4 py-8 md:py-16 max-w-4xl">
      <Skeleton className="h-10 w-64 mb-8" />
      <div className="space-y-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
        <Skeleton className="h-5 w-3/4" />
      </div>
    </div>
  </main>
);
